import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getCustomerSubscriptions,
  cancelSubscription,
  pauseSubscription,
  resumeSubscription,
  skipSubscription,
  rescheduleSubscription,
  updateSubscriptionItems,
} from '@/infrastructure/services/subscription.service';
import type { SubscriptionItemUpdate } from '@/domain/entities/subscription.entity';

export const subscriptionKeys = {
  all: ['customer-subscriptions'] as const,
  list: (customerId: string) => [...subscriptionKeys.all, customerId] as const,
};

export function useCustomerSubscriptions(customerId: string | undefined) {
  return useQuery({
    queryKey: subscriptionKeys.list(customerId ?? ''),
    queryFn: () => getCustomerSubscriptions(customerId as string),
    enabled: !!customerId,
    staleTime: 5 * 60 * 1000,
  });
}

export function useCancelSubscription(customerId: string | undefined) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      subscriptionId,
      reason,
    }: {
      subscriptionId: string;
      reason: string;
    }) => cancelSubscription(customerId as string, subscriptionId, reason),
    onSuccess: () => {
      if (customerId) {
        queryClient.invalidateQueries({ queryKey: subscriptionKeys.list(customerId) });
      }
    },
  });
}

export function usePauseSubscription(customerId: string | undefined) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      subscriptionId,
      reason,
    }: {
      subscriptionId: string;
      reason?: string;
    }) => pauseSubscription(customerId as string, subscriptionId, reason),
    onSuccess: () => {
      if (customerId) {
        queryClient.invalidateQueries({ queryKey: subscriptionKeys.list(customerId) });
      }
    },
  });
}

export function useResumeSubscription(customerId: string | undefined) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ subscriptionId }: { subscriptionId: string }) =>
      resumeSubscription(customerId as string, subscriptionId),
    onSuccess: () => {
      if (customerId) {
        queryClient.invalidateQueries({ queryKey: subscriptionKeys.list(customerId) });
      }
    },
  });
}

export function useSkipSubscription(customerId: string | undefined) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      subscriptionId,
      reason,
    }: {
      subscriptionId: string;
      reason?: string;
    }) => skipSubscription(customerId as string, subscriptionId, reason),
    onSuccess: () => {
      if (customerId) {
        queryClient.invalidateQueries({ queryKey: subscriptionKeys.list(customerId) });
      }
    },
  });
}

export function useRescheduleSubscription(customerId: string | undefined) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      subscriptionId,
      newDate,
    }: {
      subscriptionId: string;
      newDate: string;
    }) => rescheduleSubscription(customerId as string, subscriptionId, newDate),
    onSuccess: () => {
      if (customerId) {
        queryClient.invalidateQueries({ queryKey: subscriptionKeys.list(customerId) });
      }
    },
  });
}

export function useUpdateSubscriptionItems(customerId: string | undefined) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      subscriptionId,
      items,
    }: {
      subscriptionId: string;
      items: SubscriptionItemUpdate[];
    }) => updateSubscriptionItems(customerId as string, subscriptionId, items),
    onSuccess: () => {
      if (customerId) {
        queryClient.invalidateQueries({ queryKey: subscriptionKeys.list(customerId) });
      }
    },
  });
}
